<template>
  <div
    v-if="data?.src"
    ref="iframeContainer"
    class="iframe__raw rounded-2 overflow-hidden"
    :class="{
      'iframe__raw--selected': isEditMode && componentSelectedMode && componentSelectedMode.id === id,
      'iframe__raw--edit': isEditMode && componentEditMode && componentEditMode.id === id,
      'iframe__raw--present': !isEditMode,
      'iframe__raw--fullscreen': isFullScreenMode,
      relative: !isFullScreenMode,
    }"
    :style="iframeContainerStyle"
  >
    <div v-if="isComponentMounted" :style="iFrameHeight">
      <iframe ref="iframe" class="w-full h-full border-none rounded-2" :src="iframeSrc" title="iframe" />
    </div>
    <div
      v-if="(mode === 'impact' || mode === 'sharing') && !iframe?.contentWindow"
      class="absolute top-0 bottom-0 left-0 right-0 z-1 flex items-center justify-center"
      :style="{ background: themeVars.primary6 }"
      @click="
        () =>
          isEditMode && (!componentSelectedMode || componentSelectedMode.id !== id) && emit('iframe-wrapper-clicked')
      "
    >
      <NSpin :color="themeVars.primary" size="large" />
    </div>
    <div
      v-else-if="isEditMode && (!componentSelectedMode || componentSelectedMode.id !== id)"
      class="absolute top-0 left-0 right-0 bottom-0"
      @click="emit('iframe-wrapper-clicked')"
    />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  ref,
  toRef,
  toRefs,
  onMounted,
  nextTick,
  watch,
  type ComputedRef,
  inject,
} from 'vue'
import { syncRefs } from '@vueuse/core'
import { NSpin } from 'naive-ui'
import { type ComponentNode, type IFrameProps } from '@canvas-builder/types/canvas'
import { type PitcherInfo } from '@lib/types/app'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useThemeVars from '@lib/composables/useThemeVars'
import { defaultWhenUsedInSection } from '@canvas-builder/constants'
import { PRINT_SCALE_FACTOR } from '@lib/constants/config.const'
import { setupFullScreenMode } from '@canvas-builder/util/iframe.util'

const props = withDefaults(
  defineProps<{
    data?: IFrameProps
    id: string
    when_used_in_section?: ComponentNode['when_used_in_section']
    usedInSectionId?: string
  }>(),
  {
    when_used_in_section: () => defaultWhenUsedInSection,
    usedInSectionId: '',
  },
)

const emit = defineEmits<{
  (e: 'iframe-wrapper-clicked'): void
}>()

const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
  'pitcherInfo',
  computed(() => undefined),
)

const propHeight = toRef(() => props.data?.height)
const { id } = toRefs(props)
const iFramePrintHeight = ref<string | undefined>(props.data?.height)

const { activeCanvas, isEditMode, componentSelectedMode, componentEditMode, mode, isPrintDisplayMode } = useCanvas()

syncRefs(propHeight, iFramePrintHeight)

const iFrameHeight = computed(() => ({
  height: isFullScreenMode.value ? '100%' : getScaledHeight() + 'px',
}))

const getScaledHeight = () => {
  const value = iFramePrintHeight.value || props.data?.height

  if (!value) return 0

  const height = Number.parseInt(value)

  if (isNaN(height)) {
    return 0
  }

  if (isPrintDisplayMode.value) {
    return height * (1 / PRINT_SCALE_FACTOR)
  }

  return height
}

const themeVars = useThemeVars()
const iframe = ref<HTMLIFrameElement | null>(null)
const iframeContainer = ref<HTMLElement | null>(null)
const isComponentMounted = ref(false)

// Check if full screen mode is enabled for this iframe
const isFullScreenMode = computed(() => {
  return !isEditMode.value && props.data?.full_screen_mode === true
})

// Container style for the iframe wrapper
const iframeContainerStyle = computed(() => {
  if (isFullScreenMode.value) {
    // Positioning is handled by JavaScript utility
    return {}
  }
  return {
    width: props.data?.width,
  }
})

const iframeSrc = computed(() => {
  if (props.data?.src && props.data?.add_session_context) {
    const iframePayload = {
      canvas_id: activeCanvas.value?.id,
      origin: pitcherInfo.value?.apiOrigin ?? window.location.origin,
      access_token: pitcherInfo.value?.accessToken,
    }

    const encodedData = btoa(JSON.stringify(iframePayload))
    const url = new URL(props.data.src)
    const separator = url.search ? '&' : '?'
    return `${url.toString()}${separator}context=${encodedData}`
  }

  return props.data?.src || undefined
})

function cleanupIframe() {
  if (iframe.value) {
    iframe.value.src = 'about:blank'
    iframe.value = null
  }
}

// Setup full screen mode
let cleanupFullScreen: (() => void) | null = null

// Watch for full screen mode changes and setup positioning
watch(
  isFullScreenMode,
  async (newValue) => {
    await nextTick()
    if (newValue && iframeContainer.value) {
      cleanupFullScreen = setupFullScreenMode(iframeContainer.value, () => isFullScreenMode.value)
    } else if (cleanupFullScreen) {
      cleanupFullScreen()
      cleanupFullScreen = null
    }
  },
  { immediate: true },
)

onMounted(async () => {
  isComponentMounted.value = true
})

onBeforeUnmount(() => {
  cleanupIframe()
  if (cleanupFullScreen) {
    cleanupFullScreen()
  }
})
</script>

<style scoped lang="scss">
.iframe__raw {
  border: 1px solid v-bind('themeVars.primary5');

  &--selected {
    border: 2px solid v-bind('themeVars.info');
  }

  &--edit {
    border: 2px solid v-bind('themeVars.error');
  }

  &--present {
    border: none;
  }

  &--fullscreen {
    // All positioning is handled by JavaScript
    // Only keep styles that can't be set via JS or need !important to override
    border-radius: 0 !important;
    overflow: hidden !important;
    box-sizing: border-box !important;
    border: none !important;
  }
}
</style>
